import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Typography, Container, Box, TextField, Button, Modal, IconButton, InputAdornment, keyframes } from '@mui/material';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { fetchWrapperNoAuth, SERVER, sha256 } from '../../utils/helpers';
import ErrorModal from '../../components/Modals/ErrorModal';

const CustomInput = ({ type, ...props }) => {
  const { theme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.color.primary,
      },
      "&:hover fieldset": {
        borderColor: theme.color.secondary,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.color.primary,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.text.tertiary,
      fontFamily: theme.fontFamily,
      "&.Mui-focused": {
        color: theme.color.primary,
      },
    },
    "& .MuiOutlinedInput-input": {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
    },
    "& .MuiInputLabel-outlined.Mui-error": {
      color: theme.accent.error,
    },
    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.accent.error,
    },
  }

  return (
    <TextField
      {...props}
      sx={textFieldStyle}
      type={type === "password" && showPassword ? "text" : type}
      slotProps={{
        input: {
          ...props.InputProps,
          endAdornment:
            type === "password" ? (
              <InputAdornment position="end">
                <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ) : null,

        },
      }}
      customtheme={theme} // Pass theme explicitly
    />
  );
};

const VerifyPassModal = ({ open, handleClose, veriCode, setVeriCode, setModalMsg, setOpenErrorModal, email }) => {
  const { theme } = useTheme();

  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState(['', '', '', '', '']);

  const handleCodeChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...confirmationCode];
      newCode[index] = value;
      setConfirmationCode(newCode);

      // Move focus to the next input
      if (value && index < 4) {
        document.getElementById(`code-${index + 1}`).focus();
      }
    }
  };

  const verifyCode = async () => {
    const code = confirmationCode.join('');

    if (await sha256(code) === veriCode) {
      setVeriCode("GOOD");
      setIsPasswordReset(true);
    } else {
      setModalMsg("Invalid code. Please try again.");
      setOpenErrorModal(true);
    }
  };

  const resetPassword = async () => {
    const shaPassword = await sha256(password);

    const body = { password: shaPassword }
    try {
      await fetchWrapperNoAuth(`${SERVER}/falcon_reset_pass/${email.toLowerCase()}`, email.toLowerCase(), shaPassword, { method: 'POST', body: body });
      handleClose();
    } catch (error) {
      handleClose();
      setModalMsg(error.toString());
      setOpenErrorModal(true);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    handleClose();
  };

  const styles = {
    container: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '26vw',
      bgcolor: theme.background.primary,
      borderRadius: 5,
      border: `4px solid ${theme.color.primary}`,
      boxShadow: 24,
      p: 4,
      outline: 'none',
    },
    title: {
      textAlign: 'center',
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
    },
    button: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      borderRadius: '20px',
      border: `1px solid ${theme.border.light}`,
      mt: 2,
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={styles.container}>
        {isPasswordReset ? (
          <Box sx={styles.container}>
            <Typography variant="h4" component="h2" gutterBottom style={styles.title}>
              Enter new Password:
            </Typography>

            <CustomInput
              margin="normal"
              required
              fullWidth
              type="password"
              id="resetPasswordInput"
              label="Password"
              name="resetPassword"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <Button variant="contained" sx={styles.button} onClick={resetPassword} fullWidth>
              Reset
            </Button>
          </Box>
        ) : (
          <Box sx={styles.container}>
            <Typography variant="h4" component="h2" gutterBottom style={styles.title}>
              Verify Email
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
              <Typography variant="h6" component="h2" gutterBottom style={styles.title}>
                Please enter your code from email here:
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                {confirmationCode.map((digit, index) => (
                  <TextField
                    key={index}
                    id={`code-${index}`}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    variant="outlined"
                    inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                    sx={{
                      width: '40px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'transparent',
                        color: theme.text.primary,
                        '& fieldset': {
                          borderColor: theme.text.primary,
                        },
                        '&:hover fieldset': {
                          borderColor: theme.text.secondary,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.text.primary,
                        },
                      },
                    }}
                  />
                ))}
              </Box>
              <Button onClick={verifyCode} fullWidth variant="contained" sx={{ ...styles.button, backgroundColor: theme.accent.success, }}>
                Verify Code
              </Button>
              <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3 }}>
                <Button onClick={handleClose} variant="contained" sx={{ ...styles.button, backgroundColor: theme.accent.error, }}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

const Login = (props) => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { theme } = useTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [veriCode, setVeriCode] = useState('NOCODE');

  const [modalMsg, setModalMsg] = useState("");
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openVerifyModal, setOpenVerifyModal] = useState(false);

  const [wmRedirect, setWMRedirect] = useState(false);

  useEffect(() => {
    // Get current URL search params
    const urlParams = new URLSearchParams(window.location.search);

    // Check if walmartCallbackUri parameter exists
    if (urlParams.has('walmartCallbackUri')) {
      setWMRedirect(true);
    }
  }, []);


  const validateEmail = (email) => {
    const emailPattern = /^[^@]+@[^@]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  const handleLogin = async () => {
    if (!validateEmail(email.toLowerCase())) {
      setModalMsg('Invalid email');
      setOpenErrorModal(true);
      return;
    }
    if (!password) {
      setModalMsg('Please enter password');
      setOpenErrorModal(true);
      return;
    }
    setIsLoading(true);
    const shaPassword = await sha256(password);
    const body = { email: email.toLowerCase(), password: shaPassword, affiliate: (localStorage.getItem("affiliate") ?? "none") }

    try {
      const response = await fetchWrapperNoAuth(`${SERVER}/falcon_start`, email.toLowerCase(), shaPassword, { method: 'POST', body: body });
      await login(response);
      setIsLoading(false);

      // Check if walmartCallbackUri parameter exists
      if (wmRedirect) {
        window.location.href = `https://login.account.wal-mart.com/authorize?responseType=code&clientId=ed466fe8-fc58-4779-8315-a23b64d4de56&redirectUri=https://www.inventoryfalcon.com/&nonce=${Array.from({ length: 10 }, () => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 62)]).join('')}&state=${email.toLowerCase()}&clientType=seller`
      } else {
        navigate('/');
      }
    } catch (e) {
      setIsLoading(false);
      setModalMsg(e.toString());
      setOpenErrorModal(true);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (!validateEmail(email.toLowerCase())) {
      setModalMsg('Invalid email');
      setOpenErrorModal(true);
      return;
    }

    try {
      const result = await fetchWrapperNoAuth(`${SERVER}/falcon_verify_user/${email.toLowerCase()}/`, email, password);
      if (result) {
        setVeriCode(result);
        setOpenVerifyModal(true);
      } else {
        setModalMsg("User not exists. Please sign up or contact support for help.")
        setOpenErrorModal(true);
      }
    } catch (e) {
      console.error(e);
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  const hexToRgb = (hex) => {
    // Remove the # if present
    hex = hex.replace('#', '');

    // Parse the hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `${r}, ${g}, ${b}`;
  };

  // Get RGB values of secondary color
  const secondaryColorRgb = hexToRgb(theme.accent.warning);

  const pulseAnimation = keyframes`
    0% {
      box-shadow: 0 0 0 0 rgba(${secondaryColorRgb}, 0.9);
      transform: translateX(-50%) scale(1);
    }
    50% {
      box-shadow: 0 0 10px 5px rgba(${secondaryColorRgb}, 0.5);
      transform: translateX(-50%) scale(1.05);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(${secondaryColorRgb}, 0.9);
      transform: translateX(-50%) scale(1);
    }
  `;

  const styles = {
    container: {
      display: 'flex',
      height: '100%',
      alignItems: 'center',
      backgroundColor: theme.background.primary
    },
    title: {
      fontWeight: 'bold',
      fontFamily: theme.fontFamily,
      color: theme.color.primary,
    },
    button: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      borderRadius: '20px',
      backgroundColor: theme.color.primary,
      border: `1px solid ${theme.border.light}`,
      mt: 3,
      mb: 2
    },
    popup: {
      position: 'absolute',
      top: 30,
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: theme.color.secondary,
      color: theme.text.negative,
      fontFamily: theme.fontFamily,
      fontSize: '14px',
      padding: '4px 8px',
      borderRadius: '12px',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)',
      whiteSpace: 'nowrap',
      zIndex: 1000,
      animation: `${pulseAnimation} 2s infinite`
    }
  }

  if (!!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={styles.container}>
      <Container maxWidth="sm">
        <Box component="div" sx={{ mt: 1 }}>
          <Typography variant="h4" align="center" gutterBottom sx={styles.title}>
            Sign In
          </Typography>
          <CustomInput
            margin="normal"
            required
            fullWidth
            id="emailSignIn"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <CustomInput
            margin="normal"
            required
            fullWidth
            type="password"
            id="passwordInput"
            label="Password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          <Button variant="contained" sx={styles.button} onClick={handleLogin} fullWidth disabled={isLoading}>
            {isLoading ? "Loading..." : "Sign In"}
          </Button>

          <Box sx={{ display: 'flex', }}>
            <Button variant="text" sx={{ color: theme.color.primary, fontFamily: theme.fontFamily, fontSize: 14 }} onClick={handleResetPassword} fullWidth>
              forgot password?
            </Button>

            <Box sx={{ position: 'relative', width: '100%' }}>
              <Button variant="text" sx={{ color: theme.color.primary, fontFamily: theme.fontFamily, fontSize: 14 }} onClick={() => navigate('/login-super-user')} fullWidth>
                super user
                <Box sx={styles.popup}>
                  This is for Prep Centers!
                </Box>
              </Button>
            </Box>
          </Box>

          <br /><br />
          <Typography variant="p" align="center" gutterBottom sx={styles.title}>
            New here? No worries! Just enter your email address above, and we'll create your account!
          </Typography>
        </Box>
      </Container>
      <ErrorModal open={openErrorModal} setOpen={setOpenErrorModal} message={modalMsg} />

      <VerifyPassModal
        open={openVerifyModal}
        handleClose={() => setOpenVerifyModal(false)}
        veriCode={veriCode}
        setVeriCode={setVeriCode}
        setModalMsg={setModalMsg}
        setOpenErrorModal={setOpenErrorModal}
        email={email}
      />
    </Box>
  );
};

export default Login;